import classnames from 'classnames';
import * as React from 'react';
import { NotificationType, isNotification } from './context.js';
import '../button/index.js';
import { Icon } from '../icons/index.js';
import '../localization/index.js';
import '../stack/index.js';
import { icon, notification, kind, column, span, action } from './notifications.css.js';
import { useLocalization } from '../localization/localization.js';
import { Stack } from '../stack/stack.js';
import { Button } from '../button/button.js';

const NotificationIcon = ({ type }) => {
    switch (type) {
        case NotificationType.Success:
            return React.createElement(Icon, { name: "small-circular-check", className: icon });
        case NotificationType.Error:
            return React.createElement(Icon, { name: "small-circular-cross", className: icon });
        case NotificationType.Warning:
            return React.createElement(Icon, { name: "small-alert", className: icon });
        case NotificationType.Info:
            return React.createElement(Icon, { name: "small-info", className: icon });
    }
};
function usePrevious(value) {
    const ref = React.useRef();
    React.useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
function MessageComponent({ transitionStatus, message, closable = true, onCloseClick, onRequestDismiss, }) {
    const timer = React.useRef(null);
    const notification$1 = isNotification(message) ? message : undefined;
    const [actionInProgress, setActionInProgress] = React.useState(false);
    const closeLabel = useLocalization('close');
    const contentRef = React.useRef(null);
    const [style, setStyle] = React.useState();
    const previousTransitionState = usePrevious(transitionStatus);
    React.useEffect(() => {
        setStyle(getMessageTransitionStyles(transitionStatus, previousTransitionState, contentRef.current?.offsetHeight));
    }, [previousTransitionState, transitionStatus]);
    const onStopTimeout = React.useCallback(() => {
        if (timer.current !== null) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);
    const onStartTimeout = React.useCallback(() => {
        if (timer.current !== null || notification$1?.timeout === undefined || onRequestDismiss === undefined) {
            return;
        }
        timer.current = window.setTimeout(() => {
            onRequestDismiss(message.key);
        }, notification$1?.timeout);
    }, [notification$1?.timeout, onRequestDismiss, message.key]);
    React.useEffect(() => {
        onStartTimeout();
        return () => onStopTimeout();
    }, [onStartTimeout, onStopTimeout]);
    const handleCloseClick = () => {
        onCloseClick?.();
        onRequestDismiss?.(message.key);
    };
    return (React.createElement("div", { className: classnames(notification, kind[message.type]), style: style, onMouseEnter: onStopTimeout, onMouseLeave: onStartTimeout },
        React.createElement("div", { className: column }),
        React.createElement(Stack, { className: column, alignItems: "center", justifyContent: "center", gap: "medium", wrap: false, ref: contentRef },
            React.createElement(Stack, { alignItems: "center", gap: "small", wrap: false },
                React.createElement(NotificationIcon, { type: message.type }),
                React.createElement("span", { className: span },
                    React.createElement("em", null, message.title),
                    message.message !== undefined ? React.createElement(React.Fragment, null,
                        " ",
                        message.message) : null)),
            message?.action !== undefined && (React.createElement(Button, { size: "small", kind: "neutral", className: action, onClick: async () => {
                    setActionInProgress(true);
                    const close = await Promise.resolve(message.action?.onClick());
                    setActionInProgress(false);
                    if (close && onRequestDismiss !== undefined) {
                        onRequestDismiss(message.key);
                    }
                } }, message.action.title))),
        React.createElement(Stack, { justifyContent: "end", className: column }, closable && onRequestDismiss !== undefined && (React.createElement(Button, { size: "small", kind: "ghost", iconStart: "small-cross", disabled: actionInProgress, onClick: handleCloseClick, hideLabel: true }, closeLabel)))));
}
function getMessageTransitionStyles(status, prevStatus, messageContentHeight) {
    switch (status) {
        case 'entering':
            return {
                opacity: 1,
                height: `${messageContentHeight}px`,
            };
        case 'exiting':
            // static height has to be present when transitioning, but during entered we set height to auto
            if (prevStatus === 'entered') {
                return {
                    opacity: 1,
                    height: `${messageContentHeight}px`,
                };
            }
            else {
                // height 0 set in styles
                return {};
            }
        case 'entered':
            return {
                opacity: 1,
                // remove height once we are done to allow resizes of browser
                height: 'auto',
            };
        default:
            // height 0 set in styles
            return {};
    }
}

export { MessageComponent };
